/* StaysRevenueIntelligenceTab — Bundle 2.9 (5 May 2026).
 * The Stays version of the Revenue tab. Same visual rhythm as the
 * restaurant tab (KPI strip + comparison cards), but the metrics are
 * hotel-shaped: Pure Room Revenue (Rule 16), folios, ADR, RevPAR,
 * occupancy now.
 *
 * Source: /api/outlet-intel/stays/revenue/summary — pulls live data
 * from `ezee_stays_folios` (Bundle 2.7 PMS sync).
 */
import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { TrendingUp, TrendingDown, BedDouble, IndianRupee, Activity } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const PCT = (n) => `${n > 0 ? '+' : ''}${Number(n || 0).toFixed(1)}%`;

export default function StaysRevenueIntelligenceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/outlet-intel/stays/revenue/summary');
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading Stays revenue intelligence…</div>;
  if (error || !data) return <div className="text-sm" style={{ color: '#DC2626' }}>Failed to load: {error || 'no data'}</div>;

  const t = data.today || {};
  const y = data.yesterday || {};
  const w = data.wow_7d || {};
  const m = data.mom_30d || {};
  const delta = data.today_vs_yesterday_pct;
  const isUp = delta >= 0;

  return (
    <div className="space-y-4" data-testid="stays-revenue-intel">
      {/* Rule 16 banner */}
      <div className="rounded-lg p-3 text-[11px] flex items-start gap-2"
           style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}
           data-testid="stays-rev-rule16-banner">
        <Activity size={13} className="mt-0.5 flex-shrink-0" />
        <span>
          <strong>Rule 16</strong> applied: revenue shown is{' '}
          <strong>Pure Room Revenue only</strong>. F&amp;B charged to room is{' '}
          excluded so it doesn't double-count with Celeste's POS.
        </span>
      </div>

      {/* KPI strip — today + comparators (matches restaurant Revenue tab cadence) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          label="Today Room Revenue"
          value={INR(t.pure_room_revenue)}
          sub={`${t.folios || 0} folios · ADR ${INR(t.adr)}`}
          highlight
          testid="kpi-stays-today-rev"
        />
        <KpiCard
          label="Yesterday"
          value={INR(y.pure_room_revenue)}
          sub={`${y.folios || 0} folios`}
          testid="kpi-stays-yesterday-rev"
        />
        <KpiCard
          label="vs Yesterday"
          value={PCT(delta)}
          sub={isUp ? 'trending up' : 'trending down'}
          icon={isUp ? TrendingUp : TrendingDown}
          iconColor={isUp ? '#10B981' : '#DC2626'}
          testid="kpi-stays-delta"
        />
        <KpiCard
          label="WoW (7d)"
          value={INR(w.pure_room_revenue)}
          sub={`${w.folios || 0} folios · RevPAR ${INR(w.revpar)}`}
          testid="kpi-stays-wow"
        />
        <KpiCard
          label="MoM (30d)"
          value={INR(m.pure_room_revenue)}
          sub={`${m.folios || 0} folios · RevPAR ${INR(m.revpar)}`}
          testid="kpi-stays-mom"
        />
      </div>

      {/* Live state row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Live Occupancy" testid="card-stays-occ-live">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold" style={{ color: '#1B2A4A' }}>
              {data.occupied_now}/{data.total_rooms}
            </span>
            <span className="text-sm font-medium" style={{ color: '#6B7280' }}>
              ({data.occupancy_now_pct}%)
            </span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
            <BedDouble size={11} className="inline mr-1" />
            Rooms occupied right now (eZee in-house pull)
          </p>
        </Card>

        <Card title="Pure Room vs Total Stays — MTD" testid="card-stays-rule16-mtd">
          <Row label="Pure Room Revenue" value={INR(m.pure_room_revenue)} accent="#1B2A4A" />
          <Row label="Total Stays Revenue" value={INR(m.stays_revenue)} muted />
          <Row label="F&B charged to room (excluded)" value={INR(m.fnb_charged_to_room)} muted />
          <p className="text-[10px] mt-2 italic" style={{ color: '#6B7280' }}>
            Rule 16 protects the headline from double-counting.
          </p>
        </Card>

        <Card title="Per-Room Economics — MTD" testid="card-stays-perroom">
          <Row label="ADR (rev / occupied night)"
               value={INR(m.adr)} accent="#1B2A4A" />
          <Row label="RevPAR (rev / available night)"
               value={INR(m.revpar)} accent="#1B2A4A" />
          <Row label="Capacity (room-nights · 30d)"
               value={(m.room_nights_capacity || 0).toLocaleString('en-IN')} muted />
        </Card>
      </div>

      {/* Compact reconciliation footer */}
      <div className="rounded-lg p-3 text-[11px] flex items-center gap-3"
           style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}
           data-testid="stays-rev-asof">
        <IndianRupee size={11} />
        <span>
          As of {new Date(data.as_of).toLocaleString('en-IN', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
          })} · live from eZee Absolute PMS · {data.outlet_name}
        </span>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, highlight, icon: Icon, iconColor, testid }) {
  const bg = highlight ? '#1B2A4A' : 'white';
  const fg = highlight ? 'white' : '#1B2A4A';
  const muted = highlight ? '#9DB5CC' : '#6B7280';
  return (
    <div className="rounded-xl p-4"
         style={{ backgroundColor: bg, color: fg, border: highlight ? 'none' : '1px solid #E5E1DC' }}
         data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: muted }}>{label}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-semibold">{value}</p>
        {Icon && <Icon size={16} style={{ color: iconColor || muted }} />}
      </div>
      <p className="text-[11px] mt-1" style={{ color: muted }}>{sub}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value, accent, muted }) {
  return (
    <div className="flex items-baseline justify-between text-[12px] py-1"
         style={{ borderTop: '1px solid #F3F4F6' }}>
      <span style={{ color: muted ? '#9CA3AF' : '#6B7280' }}>{label}</span>
      <span className="font-semibold" style={{ color: accent || '#6B7280' }}>{value}</span>
    </div>
  );
}
